import { ref } from 'vue'
import { defineStore } from 'pinia'

import { client } from '@/graphql'
import { gidToId, graphqlUnpack } from '@/utils'
import { getBundleProducts } from '@/graphql/queries'

import type { BundleSellingPlan, BundleProduct, BundleProductVariant } from '@/types/bundle'
import type { GraphqlConnection } from '@/types/graphql'
import { useThemeStore } from '../theme'

interface SellingPlanGroupGraphql {
  id: string
  sellingPlans: GraphqlConnection<BundleSellingPlan>
}

interface BundleProductGraphql {
  id: string
  handle: string
  title: string
  sellingPlanGroups?: GraphqlConnection<SellingPlanGroupGraphql>
  variants: GraphqlConnection<BundleProductVariantGraphql>
}

interface BundleProductVariantGraphql extends Omit<BundleProductVariant, 'nid' | 'onetimePriceCents' | 'firstOrderCents' | 'firstOrderDiscountPercent'> {
  firstOrderCents: {
    value?: string
  }
  onetimePriceCents: {
    value?: string
  }
  firstOrderDiscountPercent: {
    value?: string
  }
}

const unpackMetaNumber = (meta?: { value?: string }) => {
  return meta?.value ? Number(meta.value) : undefined
}

const unpack = (product: BundleProductGraphql) => {
  if (!product) {
    return
  }
  const { id, handle, title, sellingPlanGroups, variants } = product
  const unpacked: BundleProduct = {
    id,
    nid: gidToId(id),
    handle,
    title,
    variants: variants.edges.map(i => {
      const { id, sku, title, price, onetimePriceCents, firstOrderCents, firstOrderDiscountPercent } = i.node
      const size = Number(title.match(/\d+/)?.[0])
      return {
        id,
        nid: gidToId(id),
        sku,
        title,
        size,
        price,
        priceCents: Number(price.amount) * 100,
        onetimePriceCents: unpackMetaNumber(onetimePriceCents),
        firstOrderCents: unpackMetaNumber(firstOrderCents),
        firstOrderDiscountPercent: unpackMetaNumber(firstOrderDiscountPercent)
      }
    })
      .sort((a, b) => b.size - a.size)
  }
  if (sellingPlanGroups) {
    unpacked.sellingPlans = graphqlUnpack<SellingPlanGroupGraphql>(sellingPlanGroups.edges)
      .map(sp => graphqlUnpack<BundleSellingPlan>(sp.sellingPlans.edges))
      .flat()
  }
  return unpacked
}

const TTL = 60 * 15 * 1000 // 15 minutes

export const useBundleProductsStore = defineStore('bundle/products', () => {
  const theme = useThemeStore()
  const loading = ref(false)
  const expires = ref<number>()
  const onetime = ref<BundleProduct>()
  const coffeeOnetime = ref<BundleProduct>()
  const subscription = ref<BundleProduct>()
  const coffeeSubscription = ref<BundleProduct>()

  const load = async () => {
    if (loading.value || (onetime.value && subscription.value && coffeeOnetime.value && coffeeSubscription.value)) {
      return
    }
    loading.value = true
    try {
      const { data } = await client.query<{
        onetime: BundleProductGraphql,
        coffeeOnetime: BundleProductGraphql,
        subscription: BundleProductGraphql
        coffeeSubscription: BundleProductGraphql,
      }>({
        query: getBundleProducts,
        variables: {
          locale: theme.locale.toUpperCase()
        }
      })
      onetime.value = unpack(data.onetime)
      coffeeOnetime.value = unpack(data.coffeeOnetime)
      subscription.value = unpack(data.subscription)
      coffeeSubscription.value = unpack(data.coffeeSubscription)
      expires.value = new Date().getTime() + TTL
    } catch (error) {
      console.warn(error)
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    expires.value =
      onetime.value =
      coffeeOnetime.value =
      subscription.value =
      coffeeSubscription.value = undefined
  }

  return {
    load,
    reset,
    expires,
    onetime,
    coffeeOnetime,
    subscription,
    coffeeSubscription
  }
}, {
  persist: {
    storage: window.sessionStorage,
    pick: [
      'expires',
      'onetime',
      'coffeeOnetime',
      'subscription',
      'coffeeSubscription'
    ],
    afterHydrate: ({ store: { expires = 0, reset, load } }) => {
      const now = new Date().getTime()
      const expired = (expires - now) < 0
      if (expired) {
        reset()
        load()
      }
    }
  }
})
